import { Box } from '@mui/material';
import { Theme, alpha, styled } from '@mui/material/styles';
import { LabelColor, LabelProps, LabelVariant } from './types';

export const StyledLabel = styled(Box)(
  ({
    theme,
    ownerState,
  }: {
    theme: Theme;
    ownerState: {
      color: LabelColor;
      variant: LabelVariant;
      disabled?: boolean;
      size?: LabelProps['size'];
    };
  }) => {
    const isLight = theme.palette.mode === 'light';

    const filledVariant = ownerState.variant === 'filled';
    const outlinedVariant = ownerState.variant === 'outlined';
    const softVariant = ownerState.variant === 'soft';
    const ghostVariant = ownerState.variant === 'ghost';

    const xxxSmallSize = ownerState.size === 'xxxsmall';
    const xxSmallSize = ownerState.size === 'xxsmall';
    const xSmallSize = ownerState.size === 'xsmall';
    const smallSize = ownerState.size === 'small';
    const mediumSize = ownerState.size === 'medium';
    const largeSize = ownerState.size === 'large';

    const defaultStyle = {
      ...(ownerState.color === 'default' && {
        // FILLED
        ...(filledVariant && {
          color: !isLight
            ? theme.palette.common.white
            : theme.palette.grey[500],
          backgroundColor: theme.palette.grey[200],
        }),
        // OUTLINED
        ...(outlinedVariant && {
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.text.primary}`,
        }),
        // SOFT
        ...(softVariant && {
          color: theme.palette.text.secondary,
          backgroundColor: alpha(theme.palette.grey[500], 0.16),
        }),
      }),
    };

    const colorStyle = {
      // FILLED
      ...(filledVariant && {
        color: theme.palette[ownerState.color].contrastText,
        backgroundColor: theme.palette[ownerState.color].main,
      }),
      // OUTLINED
      ...(outlinedVariant && {
        backgroundColor: theme.palette.common.white,
        color: theme.palette[ownerState.color][600],
        border: `1px solid ${
          ownerState.color === 'default'
            ? theme.palette.grey[300]
            : theme.palette[ownerState.color].main
        }`,
      }),
      // SOFT
      ...(softVariant && {
        color: theme.palette[ownerState.color][600],
        backgroundColor:
          ownerState.color === 'default'
            ? theme.palette.grey[300]
            : theme.palette[ownerState.color][100],
      }),
      // GHOST
      ...(ghostVariant && {
        backgroundColor: 'transparent',
        color: theme.palette[ownerState.color][600],
      }),
    };

    const disabledStyle = {
      ...(ownerState.disabled && {
        color: theme.palette.grey[400],
        backgroundColor: theme.palette.grey[200],
      }),
    };

    const sizeStyle = {
      ...(xxxSmallSize && {
        height: 16,
        minWidth: 16,
        fontWeight: theme.typography.fontWeightSemiBold,
        fontSize: theme.typography.pxToRem(10),
        [`& > svg, img`]: {
          width: 10,
          height: 10,
        },
      }),
      ...(xxSmallSize && {
        height: 20,
        minWidth: 20,
        fontWeight: theme.typography.fontWeightSemiBold,
        fontSize: theme.typography.pxToRem(10),
        [`& > svg, img`]: {
          width: 12,
          height: 12,
        },
      }),
      ...(xSmallSize && {
        height: 24,
        minWidth: 24,
        fontWeight: theme.typography.fontWeightSemiBold,
        fontSize: theme.typography.pxToRem(12),
        [`& > svg, img`]: {
          width: 16,
          height: 16,
        },
      }),
      ...(smallSize && {
        height: 32,
        minWidth: 32,
        fontWeight: theme.typography.fontWeightSemiBold,
        fontSize: theme.typography.pxToRem(14),
        [`& > svg, img`]: {
          width: 18,
          height: 18,
        },
      }),
      ...(mediumSize && {
        height: 40,
        minWidth: 40,
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.pxToRem(16),
        [`& > svg, img`]: {
          width: 20,
          height: 20,
        },
      }),
      ...(largeSize && {
        height: 48,
        minWidth: 48,
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.pxToRem(20),
        [`& > svg, img`]: {
          width: 24,
          height: 24,
        },
      }),
    };

    return {
      lineHeight: 0,
      borderRadius: 4,
      cursor: 'default',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      justifyContent: 'center',
      textTransform: 'capitalize',
      padding: theme.spacing(0.5),
      fontSize: theme.typography.pxToRem(10),
      fontWeight: theme.typography.fontWeightRegular,
      transition: theme.transitions.create('all', {
        duration: theme.transitions.duration.shorter,
      }),
      ...defaultStyle,
      ...colorStyle,
      ...disabledStyle,
      ...sizeStyle,
    };
  }
);
